import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import LandingPage from './pages/LandingPage';
import OneLinkLanding from './pages/OneLinkLanding';
import CompaniesHome from './pages/CompaniesHome';
import Dashboard from './pages/Dashboard';
import DemoPreview from "./components/DemoPreview";
import DemoPreviewRealEstate from "./components/DemoPreviewRealEstate";
import Settings from './pages/Settings';
import Subscription from './pages/Subscription';
import SubscriptionDemo from './components/SubscriptionDemo';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Pricing from "./pages/Pricing";
import ForTeams from "./pages/ForTeams";
import ScrollToTop from "./components/ScrollToTop"; // Adjust path as needed
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import CompanyForm from './components/CompanyForm';
import PitchBuilder from './pitch-builder-live/PitchBuilder';
import LivePreview from './components/LivePreview';
import RealEstate from './pages/RealEstate';
import InvestorTracker from './pages/InvestorTracker';
import AcceleratorTracker from './pages/AcceleratorTracker';
import WorkspaceSettings from './pages/WorkspaceSettings';
import { CalendarPage } from './pages/CalendarPage';
import { Analytics } from '@vercel/analytics/react';
import MetaTagsUpdater from './components/MetaTagsUpdater';
import Top100Investors from './components/Top100Investors';
import Blog, { blogPosts } from './components/Blog';
import BlogDetail from './components/BlogDetail';


// Component to handle redirects based on auth state
const AuthRedirect = () => {
  const { currentUser } = useAuth();
  return currentUser ? <Navigate to="/companies" /> : <Navigate to="/" />;
};

// Component to handle root path based on auth state
const RootPath = () => {
  const { currentUser } = useAuth();
  return currentUser ? <Navigate to="/companies" /> : <LandingPage />;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <SubscriptionProvider>
          <WorkspaceProvider>
            <ScrollToTop />
            <MetaTagsUpdater />
            <Analytics />
            <Layout>
            <Routes>
              <Route path="/" element={<RootPath />} />
              <Route path="/onelinklanding" element={<OneLinkLanding />} />
              <Route path="/realestate" element={<RealEstate />} />
              <Route path="/product-home" element={<Navigate to="/companies" replace />} />
              <Route path="/companies" element={
                <ProtectedRoute>
                  <CompaniesHome />
                </ProtectedRoute>
              } />
              <Route path="/investor-tracker" element={
                <ProtectedRoute>
                  <InvestorTracker />
                </ProtectedRoute>
              } />
              <Route path="/investor-tracker/calendar" element={
                <ProtectedRoute>
                  <CalendarPage />
                </ProtectedRoute>
              } />
              <Route path="/accelerator-tracker" element={
                <ProtectedRoute>
                  <AcceleratorTracker />
                </ProtectedRoute>
              } />
              <Route path="/workspace-settings" element={
                <ProtectedRoute>
                  <WorkspaceSettings />
                </ProtectedRoute>
              } />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <CompaniesHome />
                </ProtectedRoute>
              } />
              <Route path="/dashboard/new" element={
                <ProtectedRoute>
                  <CompanyForm />
                </ProtectedRoute>
              } />
              <Route path="/dashboard/:companyId" element={
                <ProtectedRoute>
                  <CompanyForm />
                </ProtectedRoute>
              } />
              <Route path="/pitch-builder/:companyId" element={
                <ProtectedRoute>
                  <PitchBuilder />
                </ProtectedRoute>
              } />
              <Route path="/demo-preview" element={<DemoPreview />} />
              <Route path="/demo-preview-realestate" element={<DemoPreviewRealEstate />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:id" element={<BlogDetail blogPosts={blogPosts} />} />
              <Route path="/blog/top-100-accelerators" element={<Top100Investors />} />
              <Route path="/top-100-accelerators" element={<Top100Investors />} />
              <Route path="/settings" element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              } />
              <Route path="/subscription" element={
                <ProtectedRoute>
                  <Subscription />
                </ProtectedRoute>
              } />
              <Route path="/subscription-demo" element={
                <ProtectedRoute>
                  <SubscriptionDemo />
                </ProtectedRoute>
              } />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/for-teams" element={<ForTeams />} />
              <Route path="/signin" element={<SignIn />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/p/:handle" element={<LivePreview />} />
              <Route path="*" element={<AuthRedirect />} />
            </Routes>
            </Layout>
          </WorkspaceProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
